import React, {
  FC,
  useContext,
  useState,
  createContext,
  useEffect,
  useMemo,
} from "react";
import {
  createTheme,
  responsiveFontSizes,
  ThemeProvider,
} from "@mui/material/styles";
import { useRouter } from "next/router";
import {
  FINANCE as FinanceRoute,
  PROCESSING as ProcessingRoute,
  MAINTENANCE as MaintenanceRoute,
} from "../config/routes";
import { Theme } from "@mui/material";
import GlobalStyles from "@mui/material/GlobalStyles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Head from "next/head";
import { useOrganizationBrand } from "@/contexts/OrganizationBrandContext";
import { createPdsTheme } from "./pdsTheme";
import { getTokens, layout, modules, stages, statusColors } from "./tokens";

const REDESIGN_FONT_STYLESHEET =
  "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Montserrat:wght@400;500;600;700&family=Oswald:wght@500;600&display=swap";

const SUCCESS_GREEN = {
  main: "#589853",
};
const WARNING_ORANGE = {
  main: "#EDA340",
};
const ERROR_RED = {
  main: "#E8605A",
};

const INFO_BLUE = {
  main: "#007bff",
};

const DARK_BACKGROUND = {
  default: "#121212",
  paper: "#1e1e1e",
};

const NON_MODULE_COLORS = {
  success: SUCCESS_GREEN,
  warning: WARNING_ORANGE,
  error: ERROR_RED,
  info: INFO_BLUE,
};

// MUI v4 defaults Checkbox/Radio/Switch to `color="secondary"`. Our `secondary`
// is intentionally near-background (CULTIVATION.light in light mode,
// CULTIVATION.dark in dark mode) so unstyled controls render invisible. Default
// them to `primary` instead, which is always the inverse of background.
// v5 moved default props from theme.props.MuiX to theme.components.MuiX.defaultProps.
const COMPONENT_PROPS = {
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },
  MuiCheckbox: { defaultProps: { color: "primary" as const } },
  MuiRadio: { defaultProps: { color: "primary" as const } },
  MuiSwitch: { defaultProps: { color: "primary" as const } },
};

const CULTIVATION = {
  dark: "#1a1a1a", // Very dark gray (almost black) for dark mode
  light: "#f5f5f5", // Very light gray (almost white) for light mode
  contrastText: "#333333", // Dark gray text for light mode, light gray text for dark mode
};
const PROCESSING = {
  dark: "#476bb7", // Processing Blue
  light: "#f0f4fa", // Very light blue-tinted gray for light mode
  contrastText: "#333333", // Dark gray text for light mode, light gray text for dark mode
};
const FINANCIALS = {
  dark: "#969cac", // Finance Grey
  light: "#f7f8fa", // Very light gray with a hint of blue for light mode
  contrastText: "#333333", // Dark gray text for light mode, light gray text for dark mode
};
const MAINTENANCE = {
  dark: "#2F7A85", // Distinct cool teal to differentiate from blue/grey modules
  light: "#E6F2F3", // Pale teal-tinted gray for light mode
  contrastText: "#333333", // Dark gray text for light mode, light gray text for dark mode
};

// Update themes
export const lightModeCultivation = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "light",
      primary: { main: CULTIVATION.dark, contrastText: "#f0f0f0" },
      secondary: {
        main: CULTIVATION.light,
        contrastText: CULTIVATION.contrastText,
      },
      background: { default: "#d7dce2" },
      ...NON_MODULE_COLORS,
    },
    components: COMPONENT_PROPS,
  })
);

export const darkModeCultivation = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: CULTIVATION.light,
        contrastText: CULTIVATION.contrastText,
      },
      secondary: { main: CULTIVATION.dark, contrastText: "#f0f0f0" },
      background: DARK_BACKGROUND,
      ...NON_MODULE_COLORS,
    },
    components: COMPONENT_PROPS,
  })
);

export const lightModeProcessing = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "light",
      primary: { main: PROCESSING.dark, contrastText: "#f0f0f0" },
      secondary: {
        main: PROCESSING.light,
        contrastText: PROCESSING.contrastText,
      },
      background: { default: "#d7dce2" },
      ...NON_MODULE_COLORS,
    },
    components: COMPONENT_PROPS,
  })
);

export const darkModeProcessing = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "dark",
      primary: { main: PROCESSING.dark, contrastText: "#f0f0f0" },
      secondary: {
        main: PROCESSING.light,
        contrastText: PROCESSING.contrastText,
      },
      background: DARK_BACKGROUND,
      ...NON_MODULE_COLORS,
    },
    components: COMPONENT_PROPS,
  })
);

export const lightModeFinance = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "light",
      primary: { main: FINANCIALS.dark, contrastText: "#f0f0f0" },
      secondary: {
        main: FINANCIALS.light,
        contrastText: FINANCIALS.contrastText,
      },
      background: { default: "#d7dce2" },
      ...NON_MODULE_COLORS,
    },
    components: COMPONENT_PROPS,
  })
);

export const darkModeFinance = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "dark",
      primary: { main: FINANCIALS.dark, contrastText: "#f0f0f0" },
      secondary: {
        main: FINANCIALS.light,
        contrastText: FINANCIALS.contrastText,
      },
      background: DARK_BACKGROUND,
      ...NON_MODULE_COLORS,
    },
    components: COMPONENT_PROPS,
  })
);

export const lightModeMaintenance = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "light",
      primary: { main: MAINTENANCE.dark, contrastText: "#f0f0f0" },
      secondary: {
        main: MAINTENANCE.light,
        contrastText: MAINTENANCE.contrastText,
      },
      background: { default: "#d7dce2" },
      ...NON_MODULE_COLORS,
    },
    components: COMPONENT_PROPS,
  })
);

export const darkModeMaintenance = responsiveFontSizes(
  createTheme({
    palette: {
      mode: "dark",
      primary: { main: MAINTENANCE.dark, contrastText: "#f0f0f0" },
      secondary: {
        main: MAINTENANCE.light,
        contrastText: MAINTENANCE.contrastText,
      },
      background: DARK_BACKGROUND,
      ...NON_MODULE_COLORS,
    },
    components: COMPONENT_PROPS,
  })
);

const getFromStorage = (key: string): string | null | undefined => {
  if (typeof window !== "undefined") {
    return window.localStorage.getItem(key);
  }
};
const setToStorage = (key: string, value: string) => {
  if (typeof window !== "undefined") {
    return window.localStorage.setItem(key, value);
  }
};

interface ThemeContextVariables {
  dark: boolean;
}

interface ThemeContextInterface extends ThemeContextVariables {
  setDark: (dark: boolean) => void;
  dark: boolean;
  theme: Theme;
  cultivation: string;
  processing: string;
  finance: string;
  maintenance: string;
}

const ThemeContext = createContext<ThemeContextInterface>({
  dark: false,
  setDark: () => {},
  theme: lightModeCultivation,
  cultivation: CULTIVATION.dark,
  processing: PROCESSING.dark,
  finance: FINANCIALS.dark,
  maintenance: MAINTENANCE.dark,
});

export const ThemeContextProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [ctx, setCtx] = useState<ThemeContextVariables>({
    dark: getFromStorage("dark") === "true",
  });
  const router = useRouter();
  const branding = useOrganizationBrand();
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  useEffect(() => {
    if (getFromStorage("dark") == null) {
      setCtx({ dark: prefersDark });
    }
  }, [prefersDark]);
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-pds-color-mode",
      ctx.dark ? "dark" : "light"
    );
  }, [ctx.dark]);
  const color = router.pathname.startsWith(ProcessingRoute)
    ? "processing"
    : router.pathname.startsWith(FinanceRoute)
    ? "finance"
    : router.pathname.startsWith(MaintenanceRoute)
    ? "maintenance"
    : "cultivation";
  const legacyTheme =
    color === "processing"
      ? ctx.dark
        ? darkModeProcessing
        : lightModeProcessing
      : color === "finance"
      ? ctx.dark
        ? darkModeFinance
        : lightModeFinance
      : color === "maintenance"
      ? ctx.dark
        ? darkModeMaintenance
        : lightModeMaintenance
      : ctx.dark
      ? darkModeCultivation
      : lightModeCultivation;

  const pdsTheme = useMemo(
    () => createPdsTheme(branding, ctx.dark ? "dark" : "light"),
    [branding, ctx.dark]
  );
  const theme = branding.redesignEnabled ? pdsTheme : legacyTheme;

  const cultivation = branding.redesignEnabled
    ? modules.cultivation
    : darkModeCultivation.palette.primary.main;
  const processing = branding.redesignEnabled
    ? modules.processing
    : darkModeProcessing.palette.primary.main;
  const finance = branding.redesignEnabled
    ? modules.finance
    : darkModeFinance.palette.primary.main;
  const maintenance = branding.redesignEnabled
    ? modules.maintenance
    : darkModeMaintenance.palette.primary.main;
  const pdsTokens = getTokens(ctx.dark ? "dark" : "light");
  return (
    <ThemeContext.Provider
      value={{
        ...ctx,
        theme,
        cultivation,
        processing,
        finance,
        maintenance,
        setDark: (dark: boolean) => {
          setToStorage("dark", dark ? "true" : "false");
          setCtx((current) => ({ ...current, dark }));
        },
      }}
    >
      <ThemeProvider theme={theme}>
        {branding.redesignEnabled && (
          <Head>
            <link rel="stylesheet" href={REDESIGN_FONT_STYLESHEET} />
          </Head>
        )}
        {branding.redesignEnabled && (
          <GlobalStyles
            styles={{
              ":root": {
                "--pds-bg": pdsTokens.bg,
                "--pds-panel": pdsTokens.panel,
                "--pds-panel-2": pdsTokens.panel2,
                "--pds-border": pdsTokens.border,
                "--pds-accent": theme.palette.primary.main,
                "--pds-module-cultivation": modules.cultivation,
                "--pds-module-processing": modules.processing,
                "--pds-module-finance": modules.finance,
                "--pds-module-maintenance": modules.maintenance,
                "--pds-stage-clone": stages.clone,
                "--pds-stage-veg": stages.veg,
                "--pds-stage-flower": stages.flower,
                "--pds-stage-drying": stages.drying,
                "--pds-status-success": statusColors.success,
                "--pds-status-warning": statusColors.warning,
                "--pds-status-error": statusColors.error,
                "--pds-topbar-height": `${layout.topbarHeight}px`,
                "--pds-sidebar-width": `${layout.sidebarWidth}px`,
              },
            }}
          />
        )}
        {ctx && children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);

export const useScreenSMDown = () =>
  useMediaQuery(useTheme().breakpoints.down("sm"));
export const useScreenMDDown = () =>
  useMediaQuery(useTheme().breakpoints.down("md"));
export const useScreenLgDown = () =>
  useMediaQuery(useTheme().breakpoints.down("lg"));
